import useI18n from '@/shared/hooks/useI18n'
import { OrganizationDocumentUpload } from '@/shared/components'
import {
  Alert,
  Button,
  GenericDialog,
  InputGroup,
  Label,
  useLatinCharacterValidation
} from '@/common'
import { useCallback, useMemo, useState } from 'react'
import { useUpdateOrganization } from '@/shared/hooks/api/organizations'
import { useSession } from '@/shared/hooks/useSession'
import { toast } from 'react-hot-toast'
import { OperatorIdLabel } from '@/components/OperatorIdLabel'

const doesStartWithPlaceOfRegistration = (value: string, placeOfRegistration: string) => {
  return (
    value.startsWith(placeOfRegistration) || value.startsWith(placeOfRegistration.toUpperCase())
  )
}

/**
 * The maximum length of the business registration number allowed by the
 * CBAM report structure is 25
 *
 * If the business registration number proided by the user starts with the
 * place of registration, they are allowed to provide a value of 25 characters
 * If not, since the ISO 2-letter country code is mandatory and we will be adding it in the XML
 * exporter, they will need to provide a value of 23 characters.
 */

const getMaxLengthForBusinessRegistrationNumber = (value: string, placeOfRegistration: string) => {
  if (doesStartWithPlaceOfRegistration(value, placeOfRegistration)) {
    return 25
  }
  return 23
}

export default function UpdateBusinessCertificateDialog({
  isOpen,
  close,
  initialState
}: {
  isOpen: boolean
  close: () => void
  initialState: {
    businessRegistrationNumber: string
    certificateFileName: string
  }
}) {
  const { t } = useI18n()
  const { session } = useSession()
  const { mutateAsync: updateOrganizationBusinessCertificate } = useUpdateOrganization(
    session.organization.id
  )

  const [state, setState] = useState(initialState)

  const { businessRegistrationNumber, certificateFileName } = state

  const { error: latinCharacterError } = useLatinCharacterValidation(
    businessRegistrationNumber,
    t('common:errors.latinCharacters')
  )

  const onBusinessRegistrationNumberChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
      setState({ ...state, [evt.target.id]: evt.target.value })
    },
    [state]
  )

  const onDocumentUploaded = useCallback(
    (fileName: string) => {
      setState({ ...state, certificateFileName: fileName })
    },
    [state]
  )

  const onDocumentDeleted = useCallback(() => {
    setState({ ...state, certificateFileName: '' })
  }, [state])

  const maxLength = useMemo(() => {
    return getMaxLengthForBusinessRegistrationNumber(
      businessRegistrationNumber,
      session.organization.placeOfRegistration ?? ''
    )
  }, [businessRegistrationNumber, session.organization.placeOfRegistration])

  const tooLongError = useMemo(() => {
    return businessRegistrationNumber.length >= maxLength
      ? t('settings:organizationDetails.updateBusinessCertificateDialog.errors.lengthError', {
          maxLength
        })
      : undefined
  }, [businessRegistrationNumber, maxLength, t])

  const onSave = useCallback(async () => {
    try {
      await updateOrganizationBusinessCertificate({
        businessRegistrationNumber,
        certificateFileName
      })
      toast.success(
        t(
          'settings:organizationDetails.updateBusinessCertificateDialog.notifications.updateSuccess'
        )
      )
      close()
    } catch (error) {
      console.error(error)
      toast.error(
        t('settings:organizationDetails.updateBusinessCertificateDialog.notifications.updateError')
      )
    }
  }, [
    updateOrganizationBusinessCertificate,
    certificateFileName,
    close,
    t,
    businessRegistrationNumber
  ])

  const error = useMemo(() => {
    if (tooLongError) {
      return tooLongError
    }
    if (latinCharacterError) {
      return latinCharacterError
    }
    return undefined
  }, [tooLongError, latinCharacterError])

  const isSaveEnabled = useMemo(() => {
    return !tooLongError && !latinCharacterError
  }, [tooLongError, latinCharacterError])

  return (
    <GenericDialog
      isOpen={isOpen}
      size="2xl"
      close={close}
      title={t('settings:organizationDetails.updateBusinessCertificateDialog.title')}
    >
      <div className="space-y-4 mb-6">
        <InputGroup
          id="businessRegistrationNumber"
          type="text"
          label={<OperatorIdLabel countryCode={session.organization.country} />}
          value={businessRegistrationNumber}
          onChange={onBusinessRegistrationNumberChange}
          hint={t(
            'settings:organizationDetails.updateBusinessCertificateDialog.fields.businessRegistrationNumber.hint'
          )}
          error={error}
        />
        <section className="space-y-2">
          <header className="text-gray-700">
            <Label>
              {t(
                'settings:organizationDetails.updateBusinessCertificateDialog.fields.businessCertificate.label'
              )}
            </Label>
            <p className="text-text-secondary italic text-sm">
              {t(
                'settings:organizationDetails.updateBusinessCertificateDialog.fields.businessCertificate.hint'
              )}
            </p>
          </header>
          <Alert type="info">
            <p>
              {t(
                'settings:organizationDetails.updateBusinessCertificateDialog.fields.businessCertificate.instruction1'
              )}
            </p>
            <p>
              {t(
                'settings:organizationDetails.updateBusinessCertificateDialog.fields.businessCertificate.instruction2'
              )}
            </p>
          </Alert>
          <OrganizationDocumentUpload
            label={t(
              'settings:organizationDetails.updateBusinessCertificateDialog.fields.businessCertificate.uploadButton'
            )}
            fileName={certificateFileName}
            onDocumentUploaded={onDocumentUploaded}
            onDocumentDeleted={onDocumentDeleted}
            onUploading={() => {}}
            disabled={false}
          />
        </section>
      </div>
      <footer className="flex justify-end gap-2">
        <Button sizeType="sm" onClick={close} className="min-w-24" variant="neutral">
          {t('common:actions.cancel')}
        </Button>
        <Button sizeType="sm" onClick={onSave} className="min-w-24" disabled={!isSaveEnabled}>
          {t('common:actions.save')}
        </Button>
      </footer>
    </GenericDialog>
  )
}
